// A chat side panel that slides in and out along the horizontal axis.
//
// Both panels use it, so the left and the right animation are identical by
// construction rather than by two copies of the same numbers.
//
// Two deliberate properties:
//   - The animated property is the flex child's WIDTH, not a transform, so the
//     chat column REFLOWS with the panel instead of being overlapped. The
//     composer stays anchored and nothing floats over the messages.
//   - Closed means "not in the tree". Children are unmounted once the exit
//     transition has run, and a panel that has never been opened never mounts
//     them at all. That is the pinned choice over merely hiding them from the
//     accessibility tree, because these panels' tabs fetch data on mount and a
//     closed tab must not be talking to the API.
//
// The state-dependent values are inline styles, not `sx`: they change every
// time `open` flips, and emotion would mint a new class per flip. The
// reduced-motion escape hatch therefore has to out-rank an inline style, which
// is the one place `!important` is earned here.
//
// The arrival light (#1347 item 2b, Matt's "starship control" panels): as a
// panel slides in, one hairline of light runs down its inner edge — the edge
// the chat is being pushed from — and fades as the panel lands. It is the
// only blue line in the product, in the Reasoning caption's blue, so it reads
// as the same intelligence arriving rather than as a new kind of decoration.
// One line, two pixels, gone in under a second and a half. Off under reduced
// motion, and switchable at SLIDE_ARRIVAL_LIGHT while it is on trial.

import React, { useEffect, useState } from 'react'
import { Box } from '@mui/material'

/** Opening is slower than closing: arriving deserves more ceremony than leaving. */
// 800ms both ways (Adam, 8 Sep, after trying 560): the same pace and movement for the
// create-page history drawer and both chat-page panels.
export const SLIDE_ENTER_MS = 800
export const SLIDE_EXIT_MS = 800
export const SLIDE_EASING = 'cubic-bezier(0.2, 0, 0, 1)'
/** How far the content is offset, in px, at the closed end of the slide. */
export const SLIDE_CONTENT_OFFSET = 24
/** The content settles in this long after the panel starts opening — the
 *  panel arrives, then what it holds — and still lands when the panel does.
 *  On the way out there is no delay: it leaves with the panel. */
export const SLIDE_CONTENT_DELAY_MS = 120
/** Whether a panel arrives with the light along its inner edge. */
export const SLIDE_ARRIVAL_LIGHT = true
/** The light outlives the slide a little: it is still running as the panel lands. */
export const SLIDE_ARRIVAL_LIGHT_MS = SLIDE_ENTER_MS + 500
/** The Reasoning caption's blue (ThinkingBlock): the one blue the chat already uses for "working". */
export const SLIDE_ARRIVAL_LIGHT_COLOR = '#00B2FF'

export interface SlidePanelProps {
  open: boolean
  /** Open width in px. Closed is always 0. */
  width: number
  /** Which edge of the chat the panel is anchored to. Sets the slide direction. */
  side: 'left' | 'right'
  children: React.ReactNode
}

export default function SlidePanel({ open, width, side, children }: SlidePanelProps) {
  // `mounted` spans the whole visible life: it turns on with `open` and off only
  // after the exit transition, so a closing panel still has something to draw.
  const [mounted, setMounted] = useState(open)
  if (open && !mounted) setMounted(true)

  // The light runs once per arrival. `arrivals` counts closed→open edges and
  // keys the light element, so each arrival is a fresh element with a fresh
  // animation; `lit` turns off when the animation ends (or on close), so a
  // panel at rest carries no light. Decided in render, not an effect: the
  // light must be in the first open frame or it starts behind the slide.
  const [prevOpen, setPrevOpen] = useState(open)
  const [arrivals, setArrivals] = useState(0)
  const [lit, setLit] = useState(false)
  if (open !== prevOpen) {
    setPrevOpen(open)
    if (open) { setArrivals(n => n + 1); setLit(true) } else setLit(false)
  }

  useEffect(() => {
    if (open || !mounted) return
    const t = setTimeout(() => setMounted(false), SLIDE_EXIT_MS)
    return () => clearTimeout(t)
  }, [open, mounted])

  const duration = open ? SLIDE_ENTER_MS : SLIDE_EXIT_MS
  const offset = side === 'left' ? -SLIDE_CONTENT_OFFSET : SLIDE_CONTENT_OFFSET
  const contentDelay = open ? SLIDE_CONTENT_DELAY_MS : 0
  const contentDuration = duration - contentDelay

  return (
    <Box
      data-testid={`slide-panel-${side}`}
      data-open={open ? 'true' : 'false'}
      style={{
        width: open ? width : 0,
        transition: `width ${duration}ms ${SLIDE_EASING}`,
      }}
      sx={{
        flexShrink: 0,
        overflow: 'hidden',
        display: 'flex',
        minHeight: 0,
        position: 'relative',
        '@media (prefers-reduced-motion: reduce)': { transition: 'none !important' },
      }}
    >
      {SLIDE_ARRIVAL_LIGHT && lit && (
        <Box
          key={arrivals}
          aria-hidden
          data-testid={`slide-panel-light-${side}`}
          onAnimationEnd={() => setLit(false)}
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            // The inner edge: where the panel meets the chat it is pushing aside.
            [side === 'left' ? 'right' : 'left']: 0,
            width: 2,
            pointerEvents: 'none',
            zIndex: 1,
            // A short band of light on a transparent track, run top to bottom.
            backgroundImage: `linear-gradient(to bottom, transparent, ${SLIDE_ARRIVAL_LIGHT_COLOR} 35%, ${SLIDE_ARRIVAL_LIGHT_COLOR} 65%, transparent)`,
            backgroundSize: '100% 45%',
            backgroundRepeat: 'no-repeat',
            boxShadow: `0 0 8px 1px ${SLIDE_ARRIVAL_LIGHT_COLOR}55`,
            animation: `slidePanelArrivalLight ${SLIDE_ARRIVAL_LIGHT_MS}ms ${SLIDE_EASING} forwards`,
            '@keyframes slidePanelArrivalLight': {
              '0%': { opacity: 0, backgroundPosition: '0 -60%' },
              '15%': { opacity: 0.9 },
              '70%': { opacity: 0.6 },
              '100%': { opacity: 0, backgroundPosition: '0 160%' },
            },
            '@media (prefers-reduced-motion: reduce)': { display: 'none' },
          }}
        />
      )}
      {/* Fixed-width inner track: the content must not reflow while the outer
          width is mid-animation, or every line of text re-wraps 60 times. */}
      <Box
        style={{
          width,
          opacity: open ? 1 : 0,
          transform: `translateX(${open ? 0 : offset}px)`,
          transition: `opacity ${contentDuration}ms ${SLIDE_EASING} ${contentDelay}ms, transform ${contentDuration}ms ${SLIDE_EASING} ${contentDelay}ms`,
        }}
        sx={{
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          '@media (prefers-reduced-motion: reduce)': { transition: 'none !important' },
        }}
      >
        {mounted ? children : null}
      </Box>
    </Box>
  )
}
